"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { getTopBannerStatus, ITopBanner, msUntilTopBannerChange } from "@/lib/topBanner";
import { GROQCTA } from "@/sanity/lib/definitions";

import arrowIcon from "@/public/icons/arrow-right-small.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const ASCII_LEFT = [
  "..#-*:%-::-.  . ::- ..=-=..: .        . :..         ",
  " @*#=*+  :.. ..  : :+:-:- .      ...::  .           ",
  "#%+ .:..  ..:---*=+ :..        .  :                 ",
  ".:= .. ..:.--=*=:=-:::   . :. :-  :  .              ",
  "   . .:..   :.-+ -      . : . ::.:                  ",
];

const ASCII_RIGHT = [
  "      .          ..  .-:-..: .     .. :.##=..= .. . ",
  "                 . .-.::: .  .  .::-++. =.-..  ..:..",
  "             ...::- ..         .. -    :.. ... .: *=",
  "             .:. :....   . :: :=  =  -:.    .  ==%.+",
  "              .: .      . : . :-:=  . .... :.+=#+#.+",
];

const CLOCK_LABEL = "UAE";
const CLOCK_FORMAT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Dubai",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

// Re-check at least once a minute, in case timers were paused (sleep, background tab)
const MAX_TIMEOUT = 60_000;

// Same idea as AsciiReveal's rest flicker: each glyph ticks on its own clock and
// jumps to a neighbour on the density ramp, so the shape stays but keeps scrambling
const ASCII_RAMP = "@%#*+=-:. "; // densest to emptiest
const ASCII_FRAME_MS = 100;
const ASCII_FLICKER_RATE = 2; // glyph changes per second
const ASCII_FLICKER_SPREAD = 1.4; // max ramp steps away from the original glyph

const hash = (n: number) => {
  const x = Math.sin(n) * 43758.5453;
  return x - Math.floor(x);
};

function AsciiDeco({ lines, className }: { lines: string[]; className?: string }) {
  const ref = useRef<HTMLPreElement>(null);

  useEffect(() => {
    // Update the text node React rendered, so React keeps owning it
    const textNode = ref.current?.firstChild;
    if (!textNode || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const source = lines.join("\n");
    const cells = Array.from(source, (char, index) => ({
      char,
      // Spaces and line breaks never change, so the outline of the art holds
      base: char === " " ? -1 : ASCII_RAMP.indexOf(char),
      speed: 0.6 + 0.8 * hash(index + 17),
      offset: hash(index) * 8,
    }));
    const start = performance.now();

    const interval = setInterval(() => {
      // Skip the copy hidden by the other breakpoint
      if (!ref.current?.offsetParent) return;

      const phase = ((performance.now() - start) / 1000) * ASCII_FLICKER_RATE;
      textNode.nodeValue = cells
        .map(({ char, base, speed, offset }, index) => {
          if (base < 0) return char;
          const tick = Math.floor(phase * speed + offset);
          const jitter = Math.round((hash(index * 1.618 + tick * 2.414) * 2 - 1) * ASCII_FLICKER_SPREAD);
          return ASCII_RAMP[Math.min(ASCII_RAMP.length - 1, Math.max(0, base + jitter))];
        })
        .join("");
    }, ASCII_FRAME_MS);

    return () => {
      clearInterval(interval);
      textNode.nodeValue = source;
    };
  }, [lines]);

  return (
    <pre
      ref={ref}
      aria-hidden
      className={cn(
        "absolute pointer-events-none select-none font-spacemono text-[2rem] leading-[1.5rem] text-white/[0.77]",
        className,
      )}>
      {lines.join("\n")}
    </pre>
  );
}

function StatusDots() {
  // Each dot runs the same loop, offset so the bright square travels across
  const dots = [
    { opacity: "opacity-100", delay: "0s" },
    { opacity: "opacity-40", delay: "-0.8s" },
    { opacity: "opacity-20", delay: "-0.4s" },
  ];

  return (
    <div
      aria-hidden
      className="flex shrink-0 gap-[0.5rem]">
      {dots.map((dot, index) => (
        <span
          key={index}
          className={cn("block w-[0.625rem] h-[0.625rem] bg-[#0f0f0f] motion-safe:animate-status-dot", dot.opacity)}
          style={{ animationDelay: dot.delay }}
        />
      ))}
    </div>
  );
}

function RollingDigit({ value, max }: { value: number; max: number }) {
  // Column holds 0..max plus a trailing 0, so max -> 0 keeps rolling up before snapping back to the top
  const [position, setPosition] = useState(value);
  const [animate, setAnimate] = useState(true);
  const previous = useRef(value);

  useEffect(() => {
    if (value === previous.current) return;
    setAnimate(true);
    setPosition(value === 0 && previous.current === max ? max + 1 : value);
    previous.current = value;
  }, [value, max]);

  return (
    <span className="block overflow-clip relative h-[1.75rem] w-[1.25rem]">
      <span
        className={cn("absolute top-0 left-0 flex flex-col", animate && "transition-transform duration-300 ease-out")}
        style={{ transform: `translateY(-${position * 1.75}rem)` }}
        onTransitionEnd={() => {
          if (position !== max + 1) return;
          setAnimate(false);
          setPosition(0);
        }}>
        {Array.from({ length: max + 2 }, (_, index) => (
          <span
            key={index}
            className="block h-[1.75rem] leading-[1.75rem]">
            {index % (max + 1)}
          </span>
        ))}
      </span>
    </span>
  );
}

function getClockTime() {
  const parts = CLOCK_FORMAT.formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "00";
  const seconds = part("second");

  return {
    hoursMinutes: `${part("hour")}:${part("minute")}`,
    secondsTens: Number(seconds[0]),
    secondsOnes: Number(seconds[1]),
  };
}

function Clock() {
  // Client-only: the server render can't know the visitor's current time
  const [time, setTime] = useState<ReturnType<typeof getClockTime> | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      setTime(getClockTime());
      timer = setTimeout(tick, 1000 - (Date.now() % 1000));
    };
    tick();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      key={time ? "ready" : "pending"}
      className={cn(
        "flex items-center font-helveticaNeue font-medium text-[2rem] leading-[1.75rem] tabular-nums text-[rgba(15,15,15,0.75)] whitespace-nowrap transition-opacity duration-300",
        time ? "opacity-100" : "opacity-0",
      )}>
      <span>
        {CLOCK_LABEL} {time?.hoursMinutes ?? "00:00"}:
      </span>
      <RollingDigit
        value={time?.secondsTens ?? 0}
        max={5}
      />
      <RollingDigit
        value={time?.secondsOnes ?? 0}
        max={9}
      />
    </div>
  );
}

// Same hover as CTAButton: the cta-wrapper / button-text-* / button-icon-* classes in scss/global/button.css
function SplitButton({ cta, className }: { cta: GROQCTA; className?: string }) {
  return (
    <CTAWrapper
      cta={cta}
      className={cn("cta-wrapper flex shrink-0", className)}>
      <span className="flex items-center h-[5rem] px-[2rem] rounded-[0.5rem] bg-[#050505] font-spacemono text-[1.75rem] leading-normal tracking-[0.07rem] uppercase text-white whitespace-nowrap">
        <span className="block overflow-clip relative">
          <span className="block button-text-initial">{cta.title}</span>
          <span
            aria-hidden
            className="block left-0 pointer-events-none button-text-hover top-[3rem]">
            {cta.title}
          </span>
        </span>
      </span>
      <span className="flex overflow-clip relative justify-center items-center w-[5rem] h-[5rem] rounded-[0.5rem] bg-[#050505]">
        <span className="flex button-icon-initial">
          <Image
            src={arrowIcon}
            alt=""
            className="w-[2rem] h-[2rem]"
          />
        </span>
        <span
          aria-hidden
          className="flex absolute inset-0 justify-center items-center -translate-x-full pointer-events-none button-icon-hover">
          <Image
            src={arrowIcon}
            alt=""
            className="w-[2rem] h-[2rem]"
          />
        </span>
      </span>
    </CTAWrapper>
  );
}

export default function TopBanner({
  data,
  initialVisible,
  ignoreSchedule = false,
}: {
  data: ITopBanner;
  initialVisible: boolean;
  // Draft mode and /banner-testing show the banner regardless of its dates
  ignoreSchedule?: boolean;
}) {
  // Pages are ISR-cached, so the server's answer can be stale: re-check against the visitor's clock
  const [visible, setVisible] = useState(initialVisible);
  const previousVisible = useRef(visible);

  useEffect(() => {
    if (ignoreSchedule) return;

    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      clearTimeout(timer);
      const now = Date.now();
      setVisible(getTopBannerStatus(data, now) === "live");

      const msUntilChange = msUntilTopBannerChange(data, now);
      if (msUntilChange !== null) {
        timer = setTimeout(check, Math.min(msUntilChange + 100, MAX_TIMEOUT));
      }
    };

    check();
    document.addEventListener("visibilitychange", check);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [data, ignoreSchedule]);

  // The banner pushes the page down, so pinned scroll sections need new positions
  useEffect(() => {
    if (previousVisible.current === visible) return;
    previousVisible.current = visible;
    ScrollTrigger.refresh();
  }, [visible]);

  if (!visible || !data.text) return null;

  return (
    <aside
      aria-label="Announcement"
      className="overflow-clip relative w-full bg-[#ff5500]">
      {/* ── Desktop ── */}
      <div className="hidden relative justify-between items-center lg:flex px-[35rem] py-[0.75rem] min-h-[6.5rem]">
        <AsciiDeco
          lines={ASCII_LEFT}
          className="left-[-17.5rem] top-[0.25rem] opacity-[0.55]"
        />
        <AsciiDeco
          lines={ASCII_RIGHT}
          className="left-[calc(100%-44.875rem)] top-[0.25rem] opacity-[0.55]"
        />

        <div className="flex relative items-center gap-[2.5rem]">
          <StatusDots />
          <Clock />
        </div>

        <p className="relative mb-0 [text-box:trim-both_cap_alphabetic] font-helveticaNeue font-medium text-[2rem] leading-[1.4] uppercase text-[rgba(5,5,5,0.75)] whitespace-nowrap">
          {data.text}
        </p>

        {data.cta ? (
          <SplitButton
            cta={data.cta}
            className="relative gap-[0.5rem]"
          />
        ) : (
          <div />
        )}
      </div>

      {/* ── Mobile ── */}
      <div className="flex relative justify-between items-center lg:hidden gap-[2rem] px-[3rem] pt-[1.5rem] pb-[1.75rem]">
        <AsciiDeco
          lines={ASCII_LEFT}
          className="left-[37.5rem] top-[-0.75rem] opacity-[0.35]"
        />

        <div className="flex relative items-center min-w-0 gap-[1.75rem]">
          <StatusDots />
          <p className="mb-0 [text-box:trim-both_cap_alphabetic] font-helveticaNeue font-medium text-[2rem] leading-[1.4] uppercase text-[#050505] [text-wrap:balance]">
            {data.mobileText || data.text}
          </p>
        </div>

        {data.cta && (
          <SplitButton
            cta={data.cta}
            className="relative gap-[0.25rem]"
          />
        )}
      </div>
    </aside>
  );
}
